import React, { useState, useEffect } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import { Card, Table, Button, Space, Input, Select, DatePicker } from "antd";
import { FaArrowLeft, FaSearch, FaDownload } from "react-icons/fa";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import {
  DEFAULT_SUFFIX_OPTIONS,
  DEFAULT_SELECTED_SUFFIXES,
  selectAllDropdownRender,
} from "../utils/suffixFilterUtils";

const { Option } = Select;
const { RangePicker } = DatePicker;

const PropertyWiseMonthlySales = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [summary, setSummary] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const [propertyGroupOptions, setPropertyGroupOptions] = useState([]);
  const [selectedPropertyGroups, setSelectedPropertyGroups] = useState([]);
  const [suffixOptions, setSuffixOptions] = useState(DEFAULT_SUFFIX_OPTIONS);
  const [selectedSuffix, setSelectedSuffix] = useState(DEFAULT_SELECTED_SUFFIXES);

  const beginRequest = useLatestRequest();

  useEffect(() => {
    const isCurrent = beginRequest();
    const fetchPropertyData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("financialYear", financialYear);
        if (searchText && searchText.trim()) {
          params.set("search", searchText.trim());
        }
        if (Array.isArray(selectedPropertyGroups) && selectedPropertyGroups.length > 0) {
          params.set("propertyGroups", selectedPropertyGroups.join(","));
        }
        if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
          params.set("suffix", selectedSuffix.join(","));
        }

        if (dateRange && dateRange.length === 2) {
          params.set("startDate", dateRange[0].format("DD-MM-YYYY"));
          params.set("endDate", dateRange[1].format("DD-MM-YYYY"));
        }

        const url = `${apiBaseUrl}/inventoryManagement/getPropertyWiseMonthlySales?${params.toString()}`;

        const response = await fetch(url, {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
        });

        const result = await response.json();
        if (!isCurrent()) return;

        if (result.success) {
          const rows = result.data || [];
          setData(rows);
          setFilteredData(rows);
          setSummary(result.summary || {});

          // Filter options (prefer API-provided lists, else derive from data)
          if (Array.isArray(result.propertyGroups)) {
            setPropertyGroupOptions(result.propertyGroups.map((g) => ({ label: g, value: g })));
          } else {
            const groups = Array.from(
              new Set((result.data || []).map((r) => r.propertyGroup || r.groupName)),
            ).filter(Boolean);
            groups.sort((a, b) => String(a).localeCompare(String(b)));
            setPropertyGroupOptions(groups.map((g) => ({ label: g, value: g })));
          }

          const apiSuffixes =
            result.suffixOptions || result.suffixes || result.suffix || null;
          if (Array.isArray(apiSuffixes) && apiSuffixes.length > 0) {
            const opts = apiSuffixes
              .map((s) => (typeof s === "string" ? s : s?.value ?? s?.label))
              .map((s) => String(s ?? "").trim())
              .filter(Boolean);
            const uniq = Array.from(new Set(opts));
            setSuffixOptions(uniq.map((s) => ({ label: s, value: s })));
          } else {
            setSuffixOptions(DEFAULT_SUFFIX_OPTIONS);
          }
        } else {
          console.error("Failed to fetch property data:", result.message);
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.error("Error fetching property data:", error);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    fetchPropertyData();
  }, [apiBaseUrl, financialYear, dateRange, selectedPropertyGroups, selectedSuffix, searchText]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("isExcel", "true");
      if (searchText && searchText.trim()) {
        params.set("search", searchText.trim());
      }
      if (Array.isArray(selectedPropertyGroups) && selectedPropertyGroups.length > 0) {
        params.set("propertyGroups", selectedPropertyGroups.join(","));
      }
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
        params.set("suffix", selectedSuffix.join(","));
      }

      if (dateRange && dateRange.length === 2) {
        params.set("startDate", dateRange[0].format("DD-MM-YYYY"));
        params.set("endDate", dateRange[1].format("DD-MM-YYYY"));
      }

      const url = `${apiBaseUrl}/inventoryManagement/getPropertyWiseMonthlySales?${params.toString()}`;

      const response = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Property_Wise_Monthly_Sales_${financialYear}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Failed to download Excel file");
      }
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const allMonths = [
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "January",
    "February",
    "March",
  ];

  const [visibleMonths, setVisibleMonths] = useState(allMonths);

  useEffect(() => {
    const summaryMonths = new Set([
      ...Object.keys(summary?.monthlyTotals || {}),
      ...Object.keys(summary?.printingCharge?.monthlyTotals || {}),
      ...Object.keys(summary?.mountingCharge?.monthlyTotals || {}),
    ]);
    const ordered = allMonths.filter((m) => summaryMonths.has(m));
    if (ordered.length > 0) {
      setVisibleMonths(ordered);
      return;
    }
    setVisibleMonths(allMonths);
  }, [summary]);

  const columns = [
    {
      title: "Code",
      dataIndex: "inventoryCode",
      key: "inventoryCode",
      width: 100,
      align: "center",
      fixed: "left",
      render: (inventoryCode, record) => {
        if (record?.isChargeRow) return null;
        return (
          <span style={{ color: "#333", fontSize: "12px", fontWeight: "bold" }}>
            {inventoryCode || "N/A"}
          </span>
        );
      },
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 200,
      align: "left",
      fixed: "left",
      render: (location) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {location || "N/A"}
        </span>
      ),
    },
    ...visibleMonths.map((month) => ({
      title: month.substr(0, 3),
      dataIndex: ["monthlySales", month],
      key: month,
      width: 80,
      align: "center",
      render: (value) => (
        <span style={{ color: "#333", fontSize: "11px" }}>
          {value ? formatCurrency(value) : "₹0"}
        </span>
      ),
    })),
    {
      title: "Total",
      dataIndex: "totalSales",
      key: "totalSales",
      width: 120,
      align: "center",
      fixed: "right",
      render: (totalSales) => (
        <span style={{ color: "#333", fontSize: "12px", fontWeight: "bold" }}>
          {totalSales ? formatCurrency(totalSales) : "₹0"}
        </span>
      ),
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            fontSize: "12px",
            fontWeight: "bold",
            padding: "8px 4px",
          }}
        />
      ),
    },
  };

  // Add summary row to filtered data
  const dataWithSummary = [
    ...filteredData,
    {
      key: "printing-charge",
      isChargeRow: true,
      inventoryCode: "",
      location: "Printing Charge",
      monthlySales: summary.printingCharge?.monthlyTotals || {},
      totalSales: summary.printingCharge?.total || 0,
    },
    {
      key: "mounting-charge",
      isChargeRow: true,
      inventoryCode: "",
      location: "Mounting Charge",
      monthlySales: summary.mountingCharge?.monthlyTotals || {},
      totalSales: summary.mountingCharge?.total || 0,
    },
    {
      key: "summary",
      inventoryCode: "TOTAL",
      location: "HO Grand Total",
      monthlySales: summary.monthlyTotals || {},
      totalSales: summary.grandTotal || 0,
    },
  ];

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
        minHeight: "100vh",
      }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Property Wise Monthly Sales
            </Space>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                padding: "12px 0",
                flexShrink: 0,
              }}
            >
              {/* Row 1: 3 filters */}
              <Space wrap size="middle">
                <Select
                  value={financialYear}
                  onChange={setFinancialYear}
                  style={{ width: 120 }}
                  placeholder="Select FY"
                >
                  {getFinancialYearOptions().map((fy) => (
                    <Option key={fy} value={fy}>
                      {fy}
                    </Option>
                  ))}
                </Select>
                <Select
                  mode="multiple"
                  placeholder="Filter by Group"
                  allowClear
                  showSearch
                  value={
                    selectedPropertyGroups.length
                      ? selectedPropertyGroups
                      : undefined
                  }
                  onChange={(v) => setSelectedPropertyGroups(v ?? [])}
                  options={propertyGroupOptions}
                  style={{ minWidth: 240 }}
                  filterOption={(input, opt) => {
                    const label = String(opt?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
                <Select
                  mode="multiple"
                  placeholder="Filter by Suffix"
                  allowClear
                  value={selectedSuffix.length ? selectedSuffix : undefined}
                  onChange={(v) => setSelectedSuffix(v ?? [])}
                  options={suffixOptions}
                  style={{ minWidth: 180 }}
                  dropdownRender={selectAllDropdownRender(
                    suffixOptions,
                    selectedSuffix,
                    setSelectedSuffix,
                  )}
                />
              </Space>

              {/* Row 2: 2 filters (+ download) */}
              <Space wrap size="middle">
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="DD-MM-YYYY"
                  placeholder={["Start Date", "End Date"]}
                  style={{ width: 220 }}
                />
                <Input
                  placeholder="Search by code or location..."
                  prefix={<FaSearch style={{ color: "#999" }} />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 260 }}
                  allowClear
                />
                <Button
                  type="primary"
                  icon={<FaDownload />}
                  loading={downloading}
                  onClick={handleDownloadExcel}
                >
                  Download Excel
                </Button>
              </Space>
            </div>
          </div>
        }
      >
        <Table
          className="pwms-report-table"
          columns={columns}
          dataSource={dataWithSummary}
          loading={loading}
          components={components}
          rowKey={(record) => record.key || record.inventoryId}
          pagination={false}
          scroll={{ x: 1500, y: 600 }}
          rowClassName={(record) => {
            if (record.inventoryCode === "TOTAL") return "total-row";
            if (record?.isChargeRow) return "charge-row";
            if (record?.isArchived === true) return "pwms-archived-row";
            return "";
          }}
          size="small"
        />
      </Card>

      <style jsx>{`
        .pwms-archived-row > td,
        .pwms-archived-row > td span {
          color: #ff4d4f !important;
        }
        .charge-row,
        .total-row {
          background-color: #f0f8ff !important;
          font-weight: bold !important;
        }
        .charge-row td,
        .total-row td {
          background-color: #f0f8ff !important;
          font-weight: bold !important;
        }
      `}</style>
    </div>
  );
};

export default PropertyWiseMonthlySales;
